import type {
  AgencyAgentStatus,
  AgentRunOutcome,
  AgentRunSideStatus,
  AgentRunStep,
  AgentRunTrigger,
  AgentState,
  AgentTaskRow,
  AgentTaskState,
} from '../../agency/api/agentStatusTypes';

// Words for the Command Center's agent status, task list and run log. Pure, so the vocabulary is
// tested once and every surface says the same thing.

export type Tone = 'success' | 'warning' | 'error' | 'info' | 'muted' | 'accent';

export const AGENT_STATE: Record<AgentState, { label: string; tone: Tone }> = {
  up_to_date: { label: 'Up to date', tone: 'success' },
  working: { label: 'Working', tone: 'accent' },
  needs_attention: { label: 'Needs attention', tone: 'error' },
  paused: { label: 'Paused', tone: 'muted' },
  not_hired: { label: 'Not hired', tone: 'muted' },
  // Not "up to date": agent-suite could not be read, and a healthy label would hide that.
  unknown: { label: 'Unknown', tone: 'warning' },
};

/** What woke the agent, read as "Started by …". */
export const TRIGGER_LABEL: Record<AgentRunTrigger, string> = {
  heartbeat: 'the 5-minute check-in',
  schedule: 'its schedule',
  event: 'new data',
  owner_message: 'an owner message',
  owner_decision: 'an owner decision',
  manual: 'Run now',
  retry: 'a retry',
  recovery: 'recovery of a stuck run',
  hire: 'hiring',
  continuation: 'the previous task finishing',
  conversation: 'a conversation reply',
  unknown: 'an unrecorded trigger',
};

export const OUTCOME: Record<AgentRunOutcome, { label: string; tone: Tone }> = {
  completed: { label: 'Completed', tone: 'success' },
  nothing_saved: { label: 'Nothing saved', tone: 'warning' },
  failed: { label: 'Failed', tone: 'error' },
  bounded: { label: 'Ran out of steps', tone: 'error' },
  waiting: { label: 'Waiting', tone: 'info' },
  not_started: { label: "Didn't start", tone: 'error' },
  nothing_to_do: { label: 'Nothing to do', tone: 'muted' },
};

const TASK_STATE: Record<AgentTaskState, { label: string; tone: Tone }> = {
  scheduled: { label: 'Scheduled', tone: 'muted' },
  working: { label: 'In progress', tone: 'accent' },
  failing: { label: 'Failing', tone: 'error' },
  queued: { label: 'Queued', tone: 'info' },
  awaiting_approval: { label: 'Awaiting approval', tone: 'warning' },
  held: { label: 'Needs review', tone: 'warning' },
  done: { label: 'Done', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
  failed: { label: 'Failed', tone: 'error' },
};

/** The task's pill: "Failing · 3 tries" once attempts have failed, the plain state otherwise. */
export function taskStateText(row: Pick<AgentTaskRow, 'state' | 'consecutiveFailures'>): {
  label: string;
  tone: Tone;
} {
  const meta = TASK_STATE[row.state];
  if (row.state === 'failing') {
    return {
      ...meta,
      label: `${meta.label} · ${row.consecutiveFailures} ${row.consecutiveFailures === 1 ? 'try' : 'tries'}`,
    };
  }
  return meta;
}

/**
 * The last attempt's error as the task row shows it. A task waiting on the owner with a saved draft
 * is not failing: the error is history, and "picks it up on the next run" was untrue (nothing runs
 * it until the owner reviews it). Live (Jenny and Joe at Mt. Diablo, Betty at PracticeNova
 * 2026-10-27) such rows sat in red for weeks. Those read as a quiet past note; anything else keeps
 * the plain error, in red.
 */
export function taskAttemptError(
  row: Pick<AgentTaskRow, 'state' | 'drafts'>,
  error: string,
  agentName: string,
  draftSaved = false,
): { text: string; past: boolean } {
  if ((row.state === 'held' || row.state === 'awaiting_approval') && row.drafts > 0) {
    return { text: 'That run stopped early. The saved draft is ready for review.', past: true };
  }
  return { text: plainRunError(error, agentName, draftSaved), past: false };
}

/** Scheduled (not yet due) and finished work are hidden until the operator asks for everything. */
export function isShownByDefault(row: Pick<AgentTaskRow, 'state'>): boolean {
  return row.state !== 'scheduled' && row.state !== 'done' && row.state !== 'cancelled';
}

const PAUSED_BY: Record<AgencyAgentStatus['pausedBy'][number], string> = {
  assignment: 'the agent is paused here',
  mission: 'its mission is paused',
  location: 'the location is inactive',
};

export function pausedByText(pausedBy: AgencyAgentStatus['pausedBy']): string {
  if (pausedBy.length === 0) return 'Paused';
  return `Paused: ${pausedBy.map((key) => PAUSED_BY[key]).join(', and ')}.`;
}

function sameDay(left: number, right: number): boolean {
  const a = new Date(left);
  const b = new Date(right);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function clockTime(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function shortDate(at: number): string {
  return new Date(at).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "45s ago" / "12 min ago" / "3 h ago" today, a date before that; "in 20 min" / "at 4:30 PM" ahead. */
export function relativeTime(at: number, now: number): string {
  const delta = now - at;
  if (delta >= 0) {
    if (!sameDay(at, now) && delta >= 3_600_000) return shortDate(at);
    if (delta < 60_000) return `${Math.max(1, Math.floor(delta / 1_000))}s ago`;
    if (delta < 3_600_000) return `${Math.floor(delta / 60_000)} min ago`;
    return `${Math.floor(delta / 3_600_000)} h ago`;
  }
  const ahead = -delta;
  if (ahead < 60_000) return 'in under a minute';
  if (ahead < 3_600_000) return `in ${Math.ceil(ahead / 60_000)} min`;
  return sameDay(at, now) ? `at ${clockTime(at)}` : shortDate(at);
}

export function durationText(ms: number): string {
  if (ms < 1_000) return `${Math.max(0, ms)} ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(ms < 10_000 ? 1 : 0)}s`;
  const minutes = Math.floor(ms / 60_000);
  return `${minutes}m ${Math.round((ms % 60_000) / 1_000)}s`;
}

export function costText(usd: number): string {
  if (usd === 0) return '$0';
  return usd < 0.01 ? '<$0.01' : `$${usd.toFixed(2)}`;
}

/**
 * "21902 in (16000 cached) / 59 out". The count in is the whole prompt: the part billed in full
 * plus what was read from or written to the cache.
 */
export function tokensText(usage: {
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheCreationTokens?: number;
}): string {
  const cached = usage.cacheReadTokens ?? 0;
  const prompt = (usage.inputTokens ?? 0) + cached + (usage.cacheCreationTokens ?? 0);
  return `${prompt} in${cached > 0 ? ` (${cached} cached)` : ''} / ${usage.outputTokens ?? 0} out`;
}

const STEP_OUTCOME: Record<AgentRunStep['outcome'], string> = {
  saved: 'saved',
  refused: 'refused',
  failed: 'failed',
};

/** One line per step: "2 · draft (gbp_post_draft) · refused: <note>". */
export function stepText(step: AgentRunStep): string {
  const tool = step.toolKey ? ` (${step.toolKey})` : '';
  // A refused or failed step's stored note is the engine's message to the agent (ids, codes,
  // instructions): the line says why in plain words, and the note sits behind the step's details.
  const plain = plainStepNote(step);
  const note = plain ? `: ${plain}` : step.note ? `: ${withoutRecordIds(step.note)}` : '';
  return `${step.loop + 1} · ${step.kind.replace(/_/g, ' ')}${tool} · ${STEP_OUTCOME[step.outcome]}${note}`;
}

/**
 * The step (as numbered on the page) that saved what a refused step tried, when a later step of the same kind
 * on the same work saved it: a check caught the first try and the agent fixed it in the same run. Undefined
 * otherwise, so a refusal nothing fixed still reads as one.
 */
export function fixedInStep(steps: readonly AgentRunStep[], index: number): number | undefined {
  const step = steps[index];
  if (!step || step.outcome !== 'refused') return undefined;
  const later = steps
    .slice(index + 1)
    .find(
      (s) =>
        s.kind === step.kind &&
        s.outcome === 'saved' &&
        (!s.taskId || !step.taskId || s.taskId === step.taskId),
    );
  return later ? later.loop + 1 : undefined;
}

/**
 * Why a refused or failed step stopped, in plain words. Undefined for a saved step (its note, if
 * any, is already plain). Applied when the run log renders, so old runs read the same way.
 */
export function plainStepNote(step: Pick<AgentRunStep, 'outcome' | 'note'>): string | undefined {
  if (step.outcome === 'saved') return undefined;
  const note = step.note ?? '';
  const rules: Array<[RegExp, string]> = [
    [
      /nothing needs a decision: answer complete/i,
      'it tried to change work that is already waiting for approval; nothing else was open, so it was told to finish',
    ],
    [
      /only current unprepared work can change|cannot change any more/i,
      'it tried to change work that is already waiting for approval or closed, so nothing was changed',
    ],
    [
      /isn't one of .+'s tools/i,
      "it asked for data this kind of work doesn't use, so it was pointed to the sources it does use",
    ],
    [
      /the same read again/i,
      'it asked for the same data again with nothing new to act on, so the run was stopped',
    ],
    [
      /stale observation[\s\S]*(turned down|latest decision|withdrawn)/i,
      'the draft used account data from before the latest decision on this kind of work, so it had to be read again',
    ],
    [
      /stale observation|old data|too old|older crawl/i,
      'the draft used data that was too old, so it had to be read again',
    ],
    [
      /unsupported|not supported|not present in its supporting evidence|claim|does not match|no observation backs|cites? no/i,
      "a figure or claim in the draft wasn't backed by the data that was read",
    ],
    [/draft check|template|layout|critic|section/i, "the draft didn't pass its checks"],
    [
      /not allowed|not available|outside (this|its|location|mission)|forbids|belongs to another/i,
      "it tried a step this work doesn't allow",
    ],
    [
      /rate.?limit|\b429\b|overloaded|usage limit|timed? ?out|timeout/i,
      'the AI service was busy or slow',
    ],
  ];
  const match = rules.find(([pattern]) => pattern.test(note));
  if (match) return match[1];
  return step.outcome === 'refused'
    ? 'a safety check stopped it before anything was saved'
    : 'this step failed';
}

/** An agent's own step note (a finish summary) can name internal record ids; the run log drops them. */
export function withoutRecordIds(text: string): string {
  return text
    .replace(/\s*\((?:commitment|observation|run|wait)_[0-9a-z]{8,}\)/gi, '')
    .replace(/\b(?:commitment|observation|run|wait)_[0-9a-z]{8,}\b/gi, 'it')
    .replace(/\s{2,}/g, ' ');
}

const OBSERVATION_KIND: Record<string, string> = {
  tool_observation: 'Data read',
  incoming_event: 'Event',
  decision: 'Decision',
  client_fact_sync: 'Client facts',
};

/** What kind of observation this is, in words; an unknown kind reads as itself. */
export function observationKindText(kind: string): string {
  return OBSERVATION_KIND[kind] ?? kind.replace(/_/g, ' ');
}

/** One line for an observation: its own summary or reason when it has one, else its content, cut short. */
export function observationSummary(content: unknown, max = 160): string {
  const record =
    content && typeof content === 'object' && !Array.isArray(content)
      ? (content as Record<string, unknown>)
      : undefined;
  const words =
    record &&
    [record.summary, record.reason, record.title, record.name].find(
      (value) => typeof value === 'string' && value.trim(),
    );
  const text = typeof words === 'string' ? words : (JSON.stringify(content) ?? '');
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/**
 * A run's error in plain English, for the Command Center's task table, timeline and run list. The
 * engine's own text (ids, internal names) stays behind the run's "Technical details" only. Known
 * failures get a sentence that says what happened and what to do; anything else gets a generic
 * line rather than the raw message. `draftSaved`: the run saved a draft before it stopped.
 */
export function plainRunError(error: string, agentName = 'The agent', draftSaved = false): string {
  if (/\brepeated read of \S+ \d+ times with nothing new/i.test(error)) {
    return `${agentName} had nothing to act on and kept re-reading the same data, so the run was stopped. Nothing was lost; the next wake with work starts fresh.`;
  }
  // Ran out of steps (the run's step budget is shared by all its work). Checked first: the engine's
  // text names a task id and a refusal code.
  if (/\biteration limit \d+ reached\b/i.test(error)) {
    const closed = /closed as "could not draft"/i.test(error);
    // A run that saved a draft before running out: the draft waits for the owner, nothing is picked up.
    if (draftSaved && !closed)
      return `${agentName} saved a draft for review before running out of steps. It is waiting for you in Needs review.`;
    const next = closed
      ? 'The task was closed and the next scheduled check starts a fresh one.'
      : `${agentName} picks it up on the next run.`;
    if (/stale_evidence/i.test(error)) {
      return `${agentName} ran out of steps. The account data behind the draft was older than the latest decision on this kind of work, so it had to be read again and the run didn't finish in time. ${next}`;
    }
    if (/refusal outstanding/i.test(error)) {
      return `${agentName} ran out of steps while fixing a draft that didn't pass its checks. ${next}`;
    }
    return `${agentName} ran out of steps before finishing. ${next}`;
  }
  const rules: Array<[RegExp, string]> = [
    // A draft refused twice in one run for a figure, date, link or claim its reads don't hold: the
    // run fails on the second refusal (the engine's text is the check's own reasons).
    [
      /not present in its supporting evidence|is not supported by its evidence|cites a read that backs nothing|has no real cited tool_observation evidence/i,
      `${agentName}'s draft named a figure or fact that wasn't in the data it read, twice, so nothing was saved. ${agentName} tries again later.`,
    ],
    // A draft or proposal the checks or the reviewer sent back, still unfixed when the run ended.
    [
      /draft failed a deterministic check|draft critic asked for a revision/i,
      `${agentName}'s draft didn't pass its checks, so nothing was saved. ${agentName} tries again later.`,
    ],
    [
      /\bmission \S+ is not active\b/i,
      `${agentName} isn't hired at this location, so this work can't run. Hire ${agentName} here, or reset to clear the old work.`,
    ],
    [
      /no valid draft-only location scope|is not allowed for location|skill is outside this mission|skill \S+ is outside/i,
      `${agentName} isn't set up for this work at this location.`,
    ],
    [
      /rate.?limit|\b429\b|overloaded|\b529\b|usage limit|credit balance|reasoner returned no usage/i,
      `The AI service was busy or over its limit. ${agentName} tries again automatically.`,
    ],
    [
      /timed? ?out|timeout|ETIMEDOUT|ECONNRESET|ECONNREFUSED|fetch failed|socket hang up/i,
      `A connection timed out. ${agentName} tries again automatically.`,
    ],
    [
      /invalid_grant|unauthori[sz]ed|\b401\b|not connected|access (was )?denied|permission denied/i,
      `A connected account (Google, Meta, …) refused access. Check this practice's connections.`,
    ],
    [
      /standing task is at risk/i,
      `${agentName} held this back: scheduled work that is due soon comes first.`,
    ],
    [
      /is not available (for this work|in this turn)|reply actions|record_fact needs|draft-only scope forbids|tool catalog/i,
      `${agentName} tried a step this work doesn't allow, so the run stopped safely.`,
    ],
  ];
  const match = rules.find(([pattern]) => pattern.test(error));
  return match
    ? match[1]
    : `${agentName} stopped on an unexpected error. Open the run for details.`;
}

/** Where an agent's status stood when "Run now" was clicked: what a new run or wake is told apart by. */
export interface RunNowMark {
  latestRunId?: string;
  activeRunId?: string;
}

export function runNowMark(run: AgentRunSideStatus | null | undefined): RunNowMark {
  return {
    ...(run?.latestRun ? { latestRunId: run.latestRun.id } : {}),
    ...(run?.activeRun ? { activeRunId: run.activeRun.runId } : {}),
  };
}

/**
 * What a "Run now" led to, once the status shows it: a run that started, or one that ended (a Run now
 * that found nothing ends as `nothing_to_do`, its reason in `error`). Undefined until then. Live (Joe,
 * Mt. Diablo 2026-10-27): the click only showed "last woke 8 minutes ago" and an unchanged run log.
 */
export function runNowResult(
  mark: RunNowMark,
  run: AgentRunSideStatus | null | undefined,
  agentName: string,
): { tone: 'info' | 'success' | 'error'; text: string } | undefined {
  if (run?.activeRun && run.activeRun.runId !== mark.activeRunId) {
    return {
      tone: 'info',
      text: `${agentName} is working on ${run.activeRun.taskTitle ? `“${run.activeRun.taskTitle}”` : 'its open work'}.`,
    };
  }
  const latest = run?.latestRun;
  if (!latest || latest.id === mark.latestRunId) return undefined;
  if (latest.outcome === 'nothing_to_do') {
    const why = (latest.error ?? '').replace(/^Nothing was due\.\s*/, '');
    return {
      tone: 'info',
      text: `${agentName} has nothing to do right now.${why ? ` ${why}` : ''}`,
    };
  }
  const label = OUTCOME[latest.outcome].label.toLowerCase();
  const task = latest.taskTitle ? ` on “${latest.taskTitle}”` : '';
  return OUTCOME[latest.outcome].tone === 'error'
    ? {
        tone: 'error',
        text: `${agentName}'s run${task} ${label === 'failed' ? 'failed' : `ended: ${label}`}. Open the run log for details.`,
      }
    : { tone: 'success', text: `${agentName} finished${task}: ${label}.` };
}
